import React, { Dispatch, ReactElement, SetStateAction, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Divider, KetchPreferences } from 'components';
import { IcoExternal } from 'icons/indexed-icons/IcoExternal';
import theme from 'themes';
import { useLocation } from 'react-router-dom';
import breakpoint from 'constants/breakpoints';
import { JBTRoutes } from 'constants/routes';
import ChevronUp from 'icons/indexed-icons/ChevronUp';
import ChevronDown from 'icons/indexed-icons/ChevronDown';
import ExternalLink from 'icons/indexed-icons/ExternalLink';
import { Link as LinkIcon } from 'icons/indexed-icons/Link';
import Internal from 'icons/indexed-icons/Internal';

interface FooterContainerProps {
  toggleContent: boolean;
  isAccessDeniedPage: boolean;
}

const Textbox = styled.div`
  font-family: ${(props) => props.theme.typography.family};
  font-size: 12px;
  line-height: 14px;
  font-weight: ${(props) => props.theme.typography.text.bodySmall.weight};
  color: ${(props) => props.theme.colors.darkGrey};
  @media (max-width: ${breakpoint.size.sm}px) {
    font-size: 12px;
  }
`;

const Link = styled.a`
  text-decoration: none;
  color: ${(props) => props.theme.colors.primaryBlue5};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 3px;
  line-height: normal;
  svg {
    height: 15px;
    padding: 1px;
  }
`;

const FooterContainer = styled.div<FooterContainerProps>`
  display: flex;
  flex-direction: row;
  gap: 6px;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0.125rem 0.0625rem 0.125rem 0.125rem rgba(0, 0, 0, 0.09);
  @media (min-width: 540px) {
    ${(props) =>
      props.isAccessDeniedPage
        ? css`
            margin-left: 0;
          `
        : props.toggleContent
        ? css`
            margin-left: 4.325rem;
          `
        : css`
            margin-left: 16.325rem;
          `}
  }
`;

const FooterOuterContainer = styled.div`
  position: fixed;
  bottom: 0px;
  z-index: 99;
  width: 100%;
  background-color: #ffffff;
`;

const FooterInnerBox = styled.div`
  display: flex;
  flex-direction: row;
  gap: 6px;
  align-items: center;
  padding: 12px;
  @media (max-width: ${breakpoint.size.sm}px) {
    padding: 0;
    margin-left: 0.5rem;
  }
`;

const FooterLinks = styled.div`
  @media (max-width: ${breakpoint.size.lg}px) {
    display: none;
  }
`;

const FooterComplianceLinks = styled.div`
  margin-right: 1rem;
  @media (min-width: ${breakpoint.size.lg + 1}px) {
    display: none;
  }
  @media (max-width: ${breakpoint.size.sm}px) {
    margin-right: 0.5rem;
  }
`;

const MenuTextbox = styled.div`
  display: flex;
  gap: 4px;
  align-items: baseline;
  font-size: 13px;
  cursor: pointer;
  @media (max-width: ${breakpoint.size.sm}px) {
    font-size: 12px;
  }
`;

const MenuItem = styled.div`
  padding: 0.75rem 0 0 0.625rem;
  &:hover {
    background-color: ${theme.colors.lightGrey2};
  }
`;

const MenuFooter = styled.div`
  border-top: 0.063rem solid ${theme.colors.lightGrey4};
  margin: 8px 10px 2px;
  padding-top: 12px;
`;

const PopupContainer = styled.ul`
  position: absolute;
  bottom: 2.3rem;
  right: 0.2rem;
  background-color: ${theme.colors.white};
  min-width: 10rem;
  padding: 0.625rem;
  border-bottom: none;
  list-style: none;
  margin: 0px;
  border: 0.5px solid ${theme.colors.primaryBlue5};
  box-shadow: 0px -4px 16px rgba(0, 0, 0, 0.15);
  border-radius: 8px 8px 0px 0px;
}
`;

interface Props {
  toggleContent?: boolean;
}

interface ComplianceLinksProps {
  setshowKetchPreferences: Dispatch<SetStateAction<boolean>>;
}
const urlWithoutFooter = [JBTRoutes.fileExpire];

const ComplianceLinks: React.FC<ComplianceLinksProps> = ({ setshowKetchPreferences }) => {
  const { t } = useTranslation(['common']);

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <FooterComplianceLinks>
      <div ref={dropdownRef}>
        <MenuTextbox onClick={handleToggle}>
          {t('jbt_compliance_links')}
          {isOpen ? <ChevronUp width="14px" /> : <ChevronDown width="14px" />}
        </MenuTextbox>
        {isOpen && (
          <PopupContainer>
            <MenuItem>
              <Textbox>
                <Link href="accessibility-policy">
                  {t('accessibility_policy')}
                  <Internal />
                </Link>
              </Textbox>
            </MenuItem>
            <MenuItem>
              <Textbox>
                <Link href="https://www.jbtmarel.com/terms-of-use" target="_blank">
                  {t('terms')} <ExternalLink />
                </Link>
              </Textbox>
            </MenuItem>
            <MenuItem>
              <Textbox>
                <Link href="https://www.jbtmarel.com/privacy-and-cookie-policy/" target="_blank">
                  {t('privacy_policy')} <ExternalLink />
                </Link>
              </Textbox>
            </MenuItem>
            <MenuItem>
              <Textbox>
                <Link onClick={() => setshowKetchPreferences((prevState) => !prevState)}>
                  {t('cookie_preferences')} <LinkIcon />
                </Link>
              </Textbox>
            </MenuItem>
            <MenuFooter>
              <Textbox>{t('version')} - V1.0</Textbox>
            </MenuFooter>
          </PopupContainer>
        )}
      </div>
    </FooterComplianceLinks>
  );
};

const Footer = ({ toggleContent }: Props): ReactElement => {
  const { t } = useTranslation(['common']);
  const [showKetchPreferences, setshowKetchPreferences] = useState(false);
  const location = useLocation();
  const isAccessDeniedPage = location.pathname === '/access-denied';

  return (
    <>
      {showKetchPreferences && <KetchPreferences />}
      {urlWithoutFooter.includes(location.pathname) ? (
        <></>
      ) : (
        <FooterOuterContainer>
          <FooterContainer toggleContent={toggleContent} isAccessDeniedPage={isAccessDeniedPage}>
            <FooterInnerBox>
              <Textbox>
                &copy; {new Date().getFullYear()}
                {t('all_rights_reserved')}
              </Textbox>
            </FooterInnerBox>
            <FooterLinks>
              <FooterInnerBox>
                <Textbox>
                  <Link href="/accessibility-policy">
                    {t('accessibility_policy')} <Internal />
                  </Link>
                </Textbox>
                <Divider />
                <Textbox>
                  <Link href="https://www.jbtmarel.com/terms-of-use/" target="_blank">
                    {t('terms')} <ExternalLink />
                  </Link>
                </Textbox>
                <Divider />
                <Textbox>
                  <Link href="https://www.jbtmarel.com/privacy-and-cookie-policy/" target="_blank">
                    {t('privacy_policy')} <ExternalLink />
                  </Link>
                </Textbox>
                <Divider />
                <Textbox>
                  <Link onClick={() => setshowKetchPreferences((prevState) => !prevState)}>
                    {t('cookie_preferences')} <LinkIcon />
                  </Link>
                </Textbox>
                <Divider />
                <Textbox>V1.0</Textbox>
              </FooterInnerBox>
            </FooterLinks>
            <ComplianceLinks setshowKetchPreferences={setshowKetchPreferences} />
          </FooterContainer>
        </FooterOuterContainer>
      )}
    </>
  );
};

export default Footer;
